import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

import { Option } from 'types';
import { BudgetData } from 'types/api/budget.type';

import { useAppSelector } from 'redux/hooks';

import { budgetDataService } from 'services/budget.service';

import { useFetch } from 'hooks';

import { formatDate } from 'helpers/formatDate';
import { mapSeasons } from 'helpers/mapSeasons';

import { routes } from 'data/routes';

import { useUsers } from 'pages/Users/Users.hooks';

import { EditForm, FormItem } from 'sections/EditForm';
import { SectionType } from 'sections/TabWrap';

dayjs.extend(customParseFormat);

// budgetData's dates come from the backend as "DD/MM/YYYY HH:mm:ss" strings, which
// dayjs's format-less fallback parser misreads as MM/DD/YYYY (see BUG-6, budget QA
// 2026-09-04). When there's no existing value to parse (new budget), keep dayjs's
// default-to-now behaviour rather than passing an explicit format, since parsing
// `undefined` with a format string produces an Invalid Date.
const parseBudgetDate = (value?: string) =>
  value ? dayjs(value, 'DD/MM/YYYY HH:mm:ss') : dayjs();

const useData = (budgetData?: BudgetData) => {
  useUsers();
  const [marketingLines, setMarketingLines] = useState<Option[]>([]);
  const [marketingLinesLoaded, setMarketingLinesLoaded] = useState(false);
  const navigate = useNavigate();
  const {
    register,
    control,
    watch,
    handleSubmit,
    setValue,
    formState: { isDirty, isValid, isSubmitting },
  } = useForm();
  const seasonsData = useAppSelector((state) => state.constants.seasons);
  const zones = useAppSelector((state) => state.constants.zones);
  const { data: typesData } = useFetch(budgetDataService.getTypes(), []);
  const seasonsToObject = mapSeasons(seasonsData);
  const seasonsMapped = Object.keys(seasonsToObject).reduce((acc, key) => {
    // @ts-ignore
    acc[key] = { label: seasonsToObject[key].season, value: seasonsToObject[key].season };
    return acc;
  }, {});

  const chosenSeason = watch('season');

  const getMarketingLine = (season: string) => {
    const seasonObj = Object.values(seasonsToObject).find((item) => item.season === season);
    const lines = seasonObj?.catalog || [];

    return lines.map((line) => ({ value: line, label: line }));
  };

  const onSubmit = (data: Record<string, string>) => {
    if (budgetData) {
      const sanitizeCode = (v?: string) =>
        typeof v === 'string' ? v.replace(/\s+/g, '').trim() : '';

      const sanitizeLine = (v?: string) =>
        typeof v === 'string' ? v.trim().replace(/\s+/g, ' ') : '';

      if (!data.season || !data.marketingLine || !data.genderCode || !data.zone) {
        toast.error('Please select Season, Marketing Line, Gender and Zone');
        return;
      }

      budgetDataService
        .edit(
          {
            ...data,
            active: Boolean(data.active),
            startDate: formatDate(new Date(data.startDate)),
            endDate: formatDate(new Date(data.endDate)),
            num: budgetData.num,
            id: budgetData.id,
          },
          budgetData.id,
          {
            season: sanitizeCode(budgetData.season),
            seasonLine: sanitizeLine(budgetData.marketingLine),
            genderCode: sanitizeCode(budgetData.genderCode),
            zone: sanitizeCode(budgetData.zone),
            type: sanitizeCode(budgetData.type),
          }
        )
        .then((res) => {
          toast.success('Budget edited successfully!');
          navigate(routes.budget);
        })
        .catch((err) => toast.error(err.message));
    } else {
      const missingField =
        !data.season || !data.marketingLine || !data.genderCode || !data.zone || !data.type;
      if (missingField) {
        toast.error('Please select Season, Marketing Line, Gender, Zone and Type');
        return;
      }

      budgetDataService
        .create({
          ...data,
          startDate: formatDate(new Date(data.startDate)),
          endDate: formatDate(new Date(data.endDate)),
        })
        .then((res) => {
          toast.success('Budget created successfully!');
          navigate(routes.budget);
        })
        .catch((err) => toast.error(err.message));
    }
  };

  const data: FormItem[] = [
    {
      name: 'name',
      value: budgetData?.name ?? '',
      label: 'Name',
    },
    {
      name: 'season',
      value: budgetData?.season ?? '',
      label: 'Season',
      type: 'select',
      options: Object.values(seasonsMapped),
    },
    {
      name: 'marketingLine',
      value: budgetData?.marketingLine ?? '',
      label: 'Marketing Line',
      type: 'select',
      options: marketingLines,
      disabled: !chosenSeason,
    },
    {
      name: 'zone',
      value: budgetData?.zone ?? '',
      label: 'Zone',
      type: 'select',
      options: zones.map(({ code }: any) => ({ value: code, label: code })),
    },

    {
      name: 'genderCode',
      value: budgetData?.genderCode ?? '',
      label: 'Gender',
      type: 'select',
      options: [
        { value: 'MU', label: 'MU' },
        { value: 'MD', label: 'MD' },
        { value: 'MB', label: 'MB' },
        { value: 'AC', label: 'AC' },
      ],
    },
    {
      name: 'type',
      value: budgetData?.type ?? '',
      label: 'Type',
      type: 'select',
      options: typesData?.map((type: string) => ({ value: type, label: type })),
    },
    {
      name: 'startDate',
      value: parseBudgetDate(budgetData?.startDate).format('YYYY-MM-DD') ?? '',
      label: 'Start Date',
      type: 'date',
    },
    {
      name: 'endDate',
      value: parseBudgetDate(budgetData?.endDate).format('YYYY-MM-DD') ?? '',
      label: 'End Date',
      type: 'date',
    },
    {
      name: 'active',
      value: budgetData?.active ?? false,
      label: 'Active',
      type: 'checkboxWithLabel',
      onChange: () => {
        setValue('active', !watch('active'));
      },
    },
  ];

  const sections: SectionType[] = [
    {
      id: 1,
      content: (
        <EditForm
          onSubmit={handleSubmit(onSubmit)}
          data={data}
          register={register}
          control={control}
          hideUndo
          hasButtons
          submitBtnLabel={budgetData ? 'Save' : 'Submit'}
          isDirty={isDirty}
          isButtonsDisabled={!isValid || isSubmitting}
        />
      ),
    },
  ];

  useEffect(() => {
    if (chosenSeason) {
      setMarketingLines(getMarketingLine(chosenSeason));
    }
  }, [chosenSeason]);

  useEffect(() => {
    if (marketingLinesLoaded && budgetData?.marketingLine) {
      setValue('marketingLine', budgetData.marketingLine);
    }
  }, [marketingLinesLoaded]);

  useEffect(() => {
    if (budgetData) {
      setValue('name', budgetData.name);
      setValue('season', budgetData.season);
      setValue('genderCode', budgetData.genderCode);
      setValue('zone', budgetData.zone);
      setValue('type', budgetData.type);
      setValue('startDate', parseBudgetDate(budgetData.startDate).format('YYYY-MM-DD'));
      setValue('endDate', parseBudgetDate(budgetData.endDate).format('YYYY-MM-DD'));
      setValue('active', !!budgetData.active);

      if (budgetData.season) {
        setMarketingLines(getMarketingLine(budgetData.season));
        setMarketingLinesLoaded(true);
        setValue('marketingLine', budgetData.marketingLine);
      }
    }
  }, [budgetData]);

  return {
    sections,
  };
};

export { useData };
